import {
  type ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { v4 as uuid } from 'uuid';

import {
  getRequiredAddressValidationSchema,
  type Address,
} from '@electricjs/arc';

import { phoneValidationSchema } from '@common';

import { emailRegex } from '@/constants/email';
import { type PaymentMethodOption } from '@/types/billing';
import {
  ShoppingCountryOption,
  type CatalogShippingMethodOption,
  type WarehouseShippingMethodOption,
} from '@/types/storefront';
import { PaymentType } from '@/redux/slices/billingApiSlice';

import { type EmployeeOption } from '@/components/People/EmployeeGroups/NewEmployeeGroupForm';

import {
  getAvailableInstallmentsCounts,
  calculateMonthsUntilExpiration,
} from '../utils/helpers';
import { getShoppingCountryOptions } from './ShoppingCountrySelector';

export enum SchemaOption {
  DEFAULT = 'DEFAULT',
  EMPLOYEE = 'EMPLOYEE',
  SHIPPING = 'SHIPPING',
  PAYMENT = 'PAYMENT',
}

type StorefrontCheckoutFormContextType = {
  storefrontCheckoutForm?: UseFormReturn<StorefrontCheckoutFormData>;
  orderKey: string;
  regenerateOrderKey: () => void;
  schemaOption: SchemaOption;
  setSchemaOption: (schemaOption: SchemaOption) => void;
};

type StorefrontCheckoutFormProviderProps = {
  children: ReactNode;
};

export type MicrosoftTenantOption = {
  label: string; // tenant.name
  value: string; // tenant.id (UUID)
};

export type StorefrontCheckoutFormData = {
  employee?: Partial<EmployeeOption>;
  employeeHasMdmEntitlement?: boolean;
  employeeHasWAutopilotEnrollmentEntitlement?: boolean;
  shippingName?: string;
  shippingEmail?: string;
  shippingPhone?: string;
  shippingAddress?: Address;
  catalogShippingMethod?: CatalogShippingMethodOption;
  warehouseShippingMethod?: WarehouseShippingMethodOption;
  notes?: string;
  paymentMethod?: PaymentMethodOption;
  paymentType?: PaymentType;
  paymentInstallmentsCount?: number;
  termsAccepted?: boolean;
  shoppingCountry?: ShoppingCountryOption;
  windowsZtpEnabled?: boolean;
  wAutopilotEnabled?: boolean;
  wAutopilotMsTenantId?: MicrosoftTenantOption;
  wAutopilotMsGroupTagName?: string;
};

const StorefrontCheckoutFormContext =
  createContext<StorefrontCheckoutFormContextType>({
    storefrontCheckoutForm: undefined,
    orderKey: '',
    regenerateOrderKey: () => {},
    schemaOption: SchemaOption.EMPLOYEE,
    setSchemaOption: () => {},
  });

export const emailSeparator = ',';

const isEmail = (value: string) => emailRegex.test(value);

export const StorefrontCheckoutFormProvider = (
  props: StorefrontCheckoutFormProviderProps
) => {
  const { t } = useTranslation('storefront');
  const [orderKey, setOrderKey] = useState(uuid());
  const [schemaOption, setSchemaOption] = useState<SchemaOption>(
    SchemaOption.DEFAULT
  );

  const schemas = {
    [SchemaOption.DEFAULT]: yup.object(),
    [SchemaOption.EMPLOYEE]: yup.object<StorefrontCheckoutFormData>().shape({
      employee: yup.object().required(t('validation.errors.selectEmployee')),
    }),
    [SchemaOption.SHIPPING]: yup.object<StorefrontCheckoutFormData>().shape({
      shippingName: yup.string().required(t('validation.errors.fieldRequired')),
      shippingEmail: yup
        .string()
        .trim()
        .required(t('validation.errors.fieldRequired'))
        .test('emails', t('validation.errors.invalidEmail'), value =>
          value.split(emailSeparator).every(email => isEmail(email.trim()))
        ),
      shippingPhone: phoneValidationSchema({
        required: true,
        invalidMessage: t('validation.errors.invalidPhone'),
        requiredMessage: t('validation.errors.phoneRequired'),
      }),
      shippingAddress: yup
        .mixed<Address>()
        .when('shoppingCountry', ([shoppingCountry], _schema) => {
          const selectedShoppingCountry =
            shoppingCountry as ShoppingCountryOption;
          if (
            selectedShoppingCountry &&
            selectedShoppingCountry.value !== 'other'
          ) {
            return getRequiredAddressValidationSchema([
              selectedShoppingCountry.value,
            ]);
          }
          return getRequiredAddressValidationSchema();
        }),
      catalogShippingMethod: yup.object(),
      warehouseShippingMethod: yup.object(),
      notes: yup.string().max(120, t('validation.errors.notesMaxLength')),
    }),
    [SchemaOption.PAYMENT]: yup.object<StorefrontCheckoutFormData>().shape({
      paymentMethod: yup
        .object()
        .required(t('validation.errors.selectPaymentMethod'))
        .when(['paymentType', 'paymentInstallmentsCount'], {
          is: (paymentType: PaymentType, paymentInstallmentsCount: number) =>
            paymentType === PaymentType.InInstallments &&
            paymentInstallmentsCount > 0,
          then: schema =>
            schema.test(
              'payment-method-expiration-check',
              t('validation.errors.paymentMethodExpiresBeforeInstallments'),
              (value, context) => {
                if (!value) return true;

                const paymentMethodOption = value as PaymentMethodOption;
                const { expirationMonth, expirationYear } =
                  paymentMethodOption.value;
                const monthsUntilExpiration = calculateMonthsUntilExpiration(
                  expirationMonth,
                  expirationYear
                );

                const { paymentInstallmentsCount } = context.parent;
                return monthsUntilExpiration >= paymentInstallmentsCount;
              }
            ),
          otherwise: schema => schema,
        }),
      termsAccepted: yup.boolean().when('paymentType', {
        is: PaymentType.InInstallments,
        then: schema =>
          schema.oneOf([true], t('validation.errors.acceptTermsRequired')),
        otherwise: schema => schema.optional(),
      }),
    }),
  };

  const defaultValues: StorefrontCheckoutFormData = {
    employee: undefined,
    shippingName: '',
    shippingPhone: '',
    shippingEmail: '',
    shippingAddress: {
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      country: '',
      zip: '',
    },
    catalogShippingMethod: undefined,
    warehouseShippingMethod: undefined,
    notes: '',
    paymentMethod: undefined,
    paymentType: PaymentType.InFull,
    paymentInstallmentsCount: Math.min(...getAvailableInstallmentsCounts()), // lowest option
    termsAccepted: false,
    shoppingCountry: getShoppingCountryOptions()[0],
    windowsZtpEnabled: undefined,
    wAutopilotEnabled: undefined,
    wAutopilotMsTenantId: undefined,
    wAutopilotMsGroupTagName: undefined,
  };

  const storefrontCheckoutForm = useForm<StorefrontCheckoutFormData>({
    resolver: yupResolver(schemas[schemaOption]),
    defaultValues,
    mode: 'onTouched',
  });

  return (
    <StorefrontCheckoutFormContext.Provider
      value={{
        storefrontCheckoutForm,
        orderKey,
        regenerateOrderKey: useCallback(() => setOrderKey(uuid()), []),
        schemaOption,
        setSchemaOption,
      }}>
      {props.children}
    </StorefrontCheckoutFormContext.Provider>
  );
};

export const useStorefrontCheckoutForm = () => {
  const {
    storefrontCheckoutForm,
    orderKey,
    regenerateOrderKey,
    schemaOption,
    setSchemaOption,
  } = useContext(StorefrontCheckoutFormContext);

  return {
    storefrontCheckoutForm,
    /** Idempotent key of the order (must be regenerated after each order submission, even if it has failed) */
    orderKey,
    regenerateOrderKey,
    schemaOption,
    setSchemaOption,
  };
};
